/**
 * Runtime Configuration System
 * This allows environment variables to be set at runtime without rebuilding the app
 */

import { RuntimeConfig } from '../types/config';

// Default configuration
const defaultConfig: RuntimeConfig = {
  API_URL: '',
  STRIPE_PUBLISHABLE_KEY: '',
  PAYPAL_CLIENT_ID: '',
  GOOGLE_CLIENT_ID: '',
  FACEBOOK_CLIENT_ID: '',
  MICROSOFT_CLIENT_ID: '',
  GITHUB_CLIENT_ID: '',
  NODE_ENV: 'production',
};

class RuntimeConfigService {
  private config: RuntimeConfig;
  private initialized = false;

  constructor() {
    this.config = { ...defaultConfig };
  }

  /**
   * Initialize the configuration system
   * This should be called once at app startup
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    // 1. Try to get config from window object (set by external script)
    if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
      this.config = { ...this.config, ...window.__RUNTIME_CONFIG__ };
    }

    // 2. Try to get config from environment variables (build-time fallback)
    if (typeof process !== 'undefined' && process.env) {
      const envConfig: Partial<RuntimeConfig> = {};

      if (process.env.REACT_APP_API_URL)
        envConfig.API_URL = process.env.REACT_APP_API_URL;
      if (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
        envConfig.STRIPE_PUBLISHABLE_KEY =
          process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (process.env.REACT_APP_PAYPAL_CLIENT_ID)
        envConfig.PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
      if (process.env.REACT_APP_GOOGLE_CLIENT_ID)
        envConfig.GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (process.env.REACT_APP_FACEBOOK_CLIENT_ID)
        envConfig.FACEBOOK_CLIENT_ID = process.env.REACT_APP_FACEBOOK_CLIENT_ID;
      if (process.env.REACT_APP_MICROSOFT_CLIENT_ID)
        envConfig.MICROSOFT_CLIENT_ID =
          process.env.REACT_APP_MICROSOFT_CLIENT_ID;
      if (process.env.REACT_APP_GITHUB_CLIENT_ID)
        envConfig.GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID;
      if (process.env.NODE_ENV) envConfig.NODE_ENV = process.env.NODE_ENV;

      this.config = { ...this.config, ...envConfig };
    }

    // 3. Try to fetch config from API endpoint
    try {
      const response = await fetch('/config.json');
      if (response.ok) {
        const apiConfig: Partial<RuntimeConfig> = await response.json();
        this.config = { ...this.config, ...apiConfig };
      }
    } catch (error) {
      // Config file not available, using defaults
    }

    // 4. Auto-detect API URL if not set
    if (!this.config.API_URL && typeof window !== 'undefined') {
      this.config.API_URL = this.detectApiUrl();
    }

    // 5. Set development mode detection
    if (
      typeof window !== 'undefined' &&
      window.location.hostname === 'localhost'
    ) {
      this.config.NODE_ENV = 'development';
    }

    this.initialized = true;
  }

  private detectApiUrl(): string {
    if (typeof window === 'undefined') return '';

    const { protocol, hostname, port } = window.location;

    // Development/localhost detection
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5175/api';
    }

    // Production: construct API URL from current domain
    const apiPort =
      protocol === 'https:'
        ? port === '443'
          ? ''
          : `:${port}`
        : port === '80'
          ? ''
          : `:${port}`;

    return `${protocol}//${hostname}${apiPort}/api`;
  }

  /**
   * Get a configuration value
   */
  get<K extends keyof RuntimeConfig>(key: K): RuntimeConfig[K] {
    return this.config[key];
  }

  /**
   * Set a configuration value at runtime
   */
  set<K extends keyof RuntimeConfig>(key: K, value: RuntimeConfig[K]): void {
    this.config[key] = value;
  }

  /**
   * Get all configuration values
   */
  getAll(): RuntimeConfig {
    return { ...this.config };
  }

  /**
   * Check if we're in development mode
   */
  isDevelopment(): boolean {
    return (
      this.config.NODE_ENV === 'development' ||
      (typeof window !== 'undefined' &&
        window.location.hostname === 'localhost')
    );
  }

  /**
   * Check if configuration is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}

// Export singleton instance
export const runtimeConfig = new RuntimeConfigService();

// Export types for external use
export type { RuntimeConfig };
